import React, { useState, useMemo } from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import ChantiersSidebarCollapsed from './ChantiersSidebarCollapsed';
import { ChantiersSidebar } from './ChantiersSidebar';

interface ChantierListItem {
  id: string;
  nom_chantier: string;
  client: string;
  statut: string;
  etat_chantier: string;
  rentabilite: number;
  categorie?: string;
  date_debut_prevue?: string;
  date_fin_estimee?: string;
  pictogramme?: string;
  couleur?: string;
}

interface ChantiersSidebarPanelProps {
  selectedChantierId: string;
  onSelectChantier: (id: string) => void;
  chantiers?: ChantierListItem[];
  loading?: boolean;
  rentabiliteLabel?: string;
}

/**
 * Panneau Chantiers Sidebar - Orchestration de l'état ouvert/fermé
 * Comme PlanningPanel : onglet fixe sur le bord droit + panneau déplié en overlay
 */
const ChantiersSidebarPanel = ({
  selectedChantierId,
  onSelectChantier,
  chantiers = [],
  loading = false,
  rentabiliteLabel = "Marge actuelle"
}: ChantiersSidebarPanelProps) => {
  const [isOpen, setIsOpen] = useState(false);

  // Calculs pour l'onglet plié
  const totalChantiers = chantiers.length;
  const chantiersEnCours = useMemo(() => {
    return chantiers.filter(c =>
      (c.etat_chantier === 'en_cours' || !c.etat_chantier) &&
      c.etat_chantier !== 'archive'
    ).length;
  }, [chantiers]);

  // Handler de sélection qui ferme le panel
  const handleSelectChantier = (id: string) => {
    onSelectChantier(id);
    setIsOpen(false);
  };

  return (
    <>
      {/* ═══════════════════════════════════════════════════════════════
          ONGLET PLIÉ - Toujours visible quand fermé
          ═══════════════════════════════════════════════════════════════ */}
      <ChantiersSidebarCollapsed
        isVisible={!isOpen}
        onExpand={() => setIsOpen(true)}
        totalChantiers={totalChantiers}
        chantiersEnCours={chantiersEnCours}
      />

      {/* ═══════════════════════════════════════════════════════════════
          OVERLAY - Fond sombre quand ouvert
          ═══════════════════════════════════════════════════════════════ */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-[55] backdrop-blur-sm"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* ═══════════════════════════════════════════════════════════════
          PANNEAU DÉPLIÉ - Visible quand ouvert
          ═══════════════════════════════════════════════════════════════ */}
      <div
        className={cn(
          // Position fixe bord droit
          'fixed right-0 top-0 bottom-0 z-[60]',
          // Dimensions (20% plus large que l'original pour meilleure lisibilité)
          'w-[240px]',
          // Animation d'entrée/sortie
          'transform transition-transform duration-300 ease-in-out',
          isOpen ? 'translate-x-0' : 'translate-x-full'
        )}
      >
        {/* Container de la sidebar - override border direction */}
        <div className="h-full bg-card border-l border-border flex flex-col shadow-2xl [&>aside]:border-r-0 [&>aside]:border-l-0 [&>aside]:w-full">
          {/* Bouton fermer - positionné en dehors à gauche */}
          <button
            onClick={() => setIsOpen(false)}
            className="absolute top-4 -left-10 p-2 rounded-lg bg-card border border-border hover:bg-muted text-muted-foreground hover:text-foreground transition-colors z-10 shadow-lg"
            title="Fermer"
          >
            <X className="w-4 h-4" />
          </button>

          {/* Sidebar content */}
          <ChantiersSidebar
            selectedChantierId={selectedChantierId}
            onSelectChantier={handleSelectChantier}
            chantiers={chantiers}
            loading={loading}
            rentabiliteLabel={rentabiliteLabel}
          />
        </div>
      </div>
    </>
  );
};

export default ChantiersSidebarPanel;
